// Time-zone helpers built on Intl only (no date libraries).
// Conventions: instants are epoch ms (UTC); local calendar dates are 'YYYY-MM-DD';
// local wall-clock times are "minutes of day" (0..1439, may overflow for after-midnight times).

export const MS_PER_MINUTE = 60_000;
export const MS_PER_HOUR = 3_600_000;
export const MS_PER_DAY = 86_400_000;

/** Anything we accept as a point in time. Strings must be ISO 8601 with offset or 'Z'. */
export type Instant = Date | number | string;

export function toMs(t: Instant): number {
  const ms = typeof t === 'number' ? t : typeof t === 'string' ? Date.parse(t) : t.getTime();
  if (!Number.isFinite(ms)) throw new RangeError(`Invalid instant: ${String(t)}`);
  return ms;
}

export function toIso(ms: number): string {
  return new Date(ms).toISOString();
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(tz: string): Intl.DateTimeFormat {
  let f = formatterCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatterCache.set(tz, f);
  }
  return f;
}

export function isValidTimeZone(tz: string): boolean {
  try {
    formatter(tz);
    return true;
  } catch {
    return false;
  }
}

export interface LocalParts {
  year: number;
  month: number; // 1..12
  day: number;
  hour: number; // 0..23
  minute: number;
  second: number;
}

export function getLocalParts(ms: number, tz: string): LocalParts {
  const out: LocalParts = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 };
  for (const p of formatter(tz).formatToParts(new Date(ms))) {
    switch (p.type) {
      case 'year': out.year = Number(p.value); break;
      case 'month': out.month = Number(p.value); break;
      case 'day': out.day = Number(p.value); break;
      case 'hour': out.hour = Number(p.value) % 24; break; // some engines emit "24"
      case 'minute': out.minute = Number(p.value); break;
      case 'second': out.second = Number(p.value); break;
      default: break;
    }
  }
  return out;
}

/** UTC offset of `tz` at instant `ms`, in minutes (e.g. -240 for EDT). */
export function tzOffsetMinutes(ms: number, tz: string): number {
  const p = getLocalParts(ms, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  const flooredMs = Math.floor(ms / 1000) * 1000;
  return Math.round((asUtc - flooredMs) / MS_PER_MINUTE);
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

export function formatLocalDate(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${pad2(month)}-${pad2(day)}`;
}

/** Local calendar date of an instant in tz. */
export function localDate(t: Instant, tz: string): string {
  const p = getLocalParts(toMs(t), tz);
  return formatLocalDate(p.year, p.month, p.day);
}

/** Local wall-clock minutes since local midnight (fractional seconds included). */
export function localMinutesOfDay(t: Instant, tz: string): number {
  const p = getLocalParts(toMs(t), tz);
  return p.hour * 60 + p.minute + p.second / 60;
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function parseLocalDate(date: string): { year: number; month: number; day: number } {
  const m = DATE_RE.exec(date);
  if (!m) throw new RangeError(`Invalid local date (expected YYYY-MM-DD): ${date}`);
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const check = new Date(Date.UTC(year, month - 1, day));
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) {
    throw new RangeError(`Invalid calendar date: ${date}`);
  }
  return { year, month, day };
}

function dateToUtcMidnight(date: string): number {
  const { year, month, day } = parseLocalDate(date);
  return Date.UTC(year, month - 1, day);
}

/** Pure calendar arithmetic on 'YYYY-MM-DD'. */
export function addDays(date: string, days: number): string {
  const d = new Date(dateToUtcMidnight(date) + days * MS_PER_DAY);
  return formatLocalDate(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

/** Whole calendar days from `a` to `b` (b - a). */
export function diffDays(a: string, b: string): number {
  return Math.round((dateToUtcMidnight(b) - dateToUtcMidnight(a)) / MS_PER_DAY);
}

/** Days since 1970-01-01 for a local date (used for stable daily rotation). */
export function dayNumber(date: string): number {
  return Math.round(dateToUtcMidnight(date) / MS_PER_DAY);
}

const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function isHHmm(s: string): boolean {
  return HHMM_RE.test(s);
}

/** 'HH:mm' -> minutes of day. */
export function parseHHmm(s: string): number {
  const m = HHMM_RE.exec(s);
  if (!m) throw new RangeError(`Invalid time (expected HH:mm): ${s}`);
  return Number(m[1]) * 60 + Number(m[2]);
}

/** minutes of day -> 'HH:mm' (wraps around midnight, rounds to the minute). */
export function formatHHmm(minutes: number): string {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
}

/**
 * UTC instant for a local wall-clock time in tz. `minutesOfDay` may be negative or >= 1440
 * (rolls into adjacent days). DST handling follows Temporal's "compatible" rule:
 * - time in a gap (spring forward) is shifted forward by the gap length (02:30 -> 03:30);
 * - ambiguous time (fall back) resolves to the earlier occurrence.
 */
export function zonedTimeToUtc(date: string, minutesOfDay: number, tz: string): number {
  const wall = dateToUtcMidnight(date) + Math.round(minutesOfDay * MS_PER_MINUTE);
  const before = tzOffsetMinutes(wall - MS_PER_DAY, tz) * MS_PER_MINUTE;
  const after = tzOffsetMinutes(wall + MS_PER_DAY, tz) * MS_PER_MINUTE;
  const candidates: number[] = [];
  for (const off of before === after ? [before] : [before, after]) {
    const t = wall - off;
    if (tzOffsetMinutes(t, tz) * MS_PER_MINUTE === off) candidates.push(t);
  }
  if (candidates.length > 0) return Math.min(...candidates);
  // Gap: interpret with the pre-transition offset, which lands after the jump.
  return wall - before;
}

/** UTC instant of local midnight (or the first valid instant of that day). */
export function startOfLocalDay(date: string, tz: string): number {
  return zonedTimeToUtc(date, 0, tz);
}

/** Length of a local day in minutes (1380 / 1440 / 1500 around DST changes). */
export function localDayLengthMinutes(date: string, tz: string): number {
  return (startOfLocalDay(addDays(date, 1), tz) - startOfLocalDay(date, tz)) / MS_PER_MINUTE;
}

export function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}
